/**
 * filter-row-controls.tsx — kontrolki w WIERSZU FILTRÓW `ConfigurableTable`.
 *
 * Jeden wspólny wygląd dla wszystkich kontrolek filtra (task #9): identyczna
 * WYSOKOŚĆ, BORDER-RADIUS i FOCUS (pierścień z tokenów akcentu), dark mode /
 * white-label „za darmo". Osiągnięte przez oparcie KAŻDEJ kontrolki na prymitywach
 * biblioteki UI (`Input`, `Dropdown`) — zero natywnego `<select>`/`<input>` z własną
 * stylistyką i natywnym (czarnym/niebieskim) focusem.
 *
 * Dyspozytor `ColumnFilterControl` wybiera właściwą kontrolkę po kategorii filtra
 * kolumny (`headerFilter.category`) i buduje `ColumnHeaderFilter` — semantyka
 * filtrowania i kształt emitowanego filtra bez zmian względem poprzedniej wersji
 * (wstecznie zgodne z kontraktem `ConfigurableTable`).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';

import { Checkbox } from '@/components/Checkbox';
import { DatePicker } from '@/components/DatePicker';
import { Dropdown, type DropdownOption } from '@/components/Dropdown';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';

import { FILTER_CONTROL_SIZE_CLASS } from './filter-row/control-classes';
import type { ColumnFilterCategory, ColumnHeaderFilter } from './types';

// ============================================================
// SEARCH (text) — pole „Szukaj…"
// ============================================================

interface SearchFilterRowControlProps {
  columnLabel: string;
  value: string;
  onChange: (value: string) => void;
}

/**
 * Pole tekstowe „Szukaj…" oparte o prymityw `Input` (ikona lupy jako `prefixIcon`,
 * przycisk czyszczenia jako `IconButton` nakładany na prawą krawędź). Ten sam
 * radius/wysokość/focus co pozostałe kontrolki filtra.
 *
 * Kontener nosi stały znacznik `data-filter-kind="legacy-search"` — po nim testy rozpoznają martwe
 * pole „Szukaj…" na listach sterowanych przez serwer (nic tam nie filtruje), bez zgadywania po
 * braku `data-testid`.
 */
export function SearchFilterRowControl({
  columnLabel,
  value,
  onChange,
}: SearchFilterRowControlProps) {
  const { t } = useTranslation();
  return (
    <div className="relative" data-filter-kind="legacy-search">
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder=""
        aria-label={columnLabel}
        prefixIcon={<Search className="h-3.5 w-3.5" />}
        className={`${FILTER_CONTROL_SIZE_CLASS} ${value ? 'pr-9' : ''}`}
      />
      {value && (
        <IconButton
          size="sm"
          aria-label={t('ui.configurableTable.clearSearch')}
          onClick={() => onChange('')}
          className="absolute right-1 top-1/2 -translate-y-1/2"
        >
          <X />
        </IconButton>
      )}
    </div>
  );
}

// ============================================================
// SELECT (stała lista opcji) — dropdown exact-match (tryb 'values')
// ============================================================

interface SelectFilterRowControlProps {
  columnLabel: string;
  field: string;
  options: Array<{ value: string; label: string }>;
  filter: ColumnHeaderFilter | undefined;
  onApply: (next: ColumnHeaderFilter | null) => void;
  testId?: string;
}

/**
 * Dropdown filtra dla kolumn kategorii `select` (stała lista opcji). PRÓBKA #14:
 * własny `Dropdown` (Radix) zamiast `Select` — lista opcji jest w pełni stylowana
 * (nasz panel, hover/selected w akcencie), koniec z natywnym rozwinięciem OS. Pusta
 * opcja „Wszystkie" = brak filtra; wybór wartości emituje `mode:'values'`
 * (exact-match, bez uderzania w API distinct).
 */
export function SelectFilterRowControl({
  columnLabel,
  field,
  options,
  filter,
  onApply,
  testId,
}: SelectFilterRowControlProps) {
  const { t } = useTranslation();
  const selectOptions: DropdownOption[] = [
    { value: '', label: t('ui.configurableTable.selectFilter.all') },
    ...options.map((o) => ({ value: o.value, label: o.label })),
  ];
  const value = filter?.mode === 'values' ? String(filter.selectedValues?.[0] ?? '') : '';
  return (
    <Dropdown
      options={selectOptions}
      value={value}
      onChange={(val) => {
        if (!val) {
          onApply(null);
        } else {
          onApply({ field, mode: 'values', selectedValues: [val] });
        }
      }}
      aria-label={columnLabel}
      data-testid={testId}
      className={FILTER_CONTROL_SIZE_CLASS}
    />
  );
}

// ============================================================
// BOOLEAN — dropdown Wszystkie / Tak / Nie
// ============================================================

export type BooleanFilterState = 'all' | 'true' | 'false';

/**
 * Odczytuje stan tri-state z `ColumnHeaderFilter` kolumny — WYŁĄCZNIE `mode:'condition'` +
 * `operator:'eq'` liczy się jako "Tak"/"Nie" (kształt identyczny z tym, co emituje ta
 * kontrolka i zgodny z shape'em walidowanym server-side, `EXT_VALUE_SHAPE_VALIDATORS.
 * boolean` / `CORE_VALUE_SHAPE_REGEX.boolean` w `apps/api/lib/contractors/apply-header-
 * filters.ts` — `conditionValue` string `'true'`/`'false'`). Brak filtra lub inny kształt
 * (np. filtr zaaplikowany wcześniej przez popup nagłówka w trybie `values`) → "Wszystkie".
 */
export function readBooleanFilterState(filter: ColumnHeaderFilter | undefined): BooleanFilterState {
  if (filter?.mode === 'condition' && filter.operator === 'eq') {
    if (filter.conditionValue === 'true') return 'true';
    if (filter.conditionValue === 'false') return 'false';
  }
  return 'all';
}

interface BooleanFilterRowControlProps {
  /** Etykieta kolumny (nagłówek) — wchodzi do `aria-label` kontrolki, żeby AT rozróżniał
   *  kilka filtrów w tej samej tabeli. */
  columnLabel: string;
  value: BooleanFilterState;
  onChange: (next: BooleanFilterState) => void;
  /** `data-testid` — bez `testIdPrefix` na `ConfigurableTable` brak testidów. */
  testIdPrefix?: string;
}

/**
 * Kontrolka filtra (Wszystkie/Tak/Nie) dla kolumn kategorii `boolean` — dropdown
 * (własny `Dropdown`/Radix, w pełni stylowana lista opcji). Emisja: "Wszystkie"
 * czyści filtr (`onChange('all')` → wołający robi `null`); "Tak"/"Nie" budują
 * `{ mode:'condition', operator:'eq', conditionValue:'true'|'false' }` — DOKŁADNIE
 * ten sam kształt co zakładka "Warunek" w popupie nagłówka (identyczna ścieżka
 * serwerowa, zero rozjazdu kształtu).
 */
export function BooleanFilterRowControl({
  columnLabel,
  value,
  onChange,
  testIdPrefix,
}: BooleanFilterRowControlProps) {
  const { t } = useTranslation();
  const options: DropdownOption[] = [
    { value: 'all', label: t('ui.configurableTable.boolFilter.all') },
    { value: 'true', label: t('ui.configurableTable.boolFilter.yes') },
    { value: 'false', label: t('ui.configurableTable.boolFilter.no') },
  ];
  return (
    <Dropdown
      options={options}
      value={value}
      onChange={(val) => onChange(val as BooleanFilterState)}
      aria-label={`${t('ui.configurableTable.boolFilter.ariaLabel')}: ${columnLabel}`}
      data-testid={testIdPrefix}
      className={FILTER_CONTROL_SIZE_CLASS}
    />
  );
}

// ============================================================
// DATE — zakres od–do lub pojedyncza data (równość dnia)
// ============================================================

interface DateRangeFilterRowControlProps {
  /** Etykieta kolumny — do `aria-label` pól (AT rozróżnia wiele filtrów w tabeli). */
  columnLabel: string;
  /** Id kolumny — pole `field` emitowanego filtra. */
  field: string;
  /** Aktualny filtr tej kolumny (lub `undefined`, gdy brak). */
  filter: ColumnHeaderFilter | undefined;
  /** Emituje nowy filtr (lub `null`, gdy pusty → wyczyść). */
  onApply: (next: ColumnHeaderFilter | null) => void;
  testIdPrefix?: string;
}

/**
 * Filtr daty dla kolumn kategorii `date`. Dwa tryby (przełącznik „Pojedyncza data"):
 *  - domyślnie ZAKRES od–do: dwa pola daty (prymityw `Input type=date`) → filtr
 *    `mode:'range'` (rangeMin/rangeMax), inkluzywnie dzień po dniu.
 *  - „Pojedyncza data": jedno pole daty, filtr = RÓWNOŚĆ dnia. Emitujemy nadal
 *    `mode:'range'` z `rangeMin === rangeMax` + flagą `singleDate:true`, a
 *    `passesHeaderFilter` porównuje po dacie (część dzienna, bez timestampu).
 *
 * Accessor kolumny powinien zwracać datę w formacie `YYYY-MM-DD`.
 */
export function DateRangeFilterRowControl({
  columnLabel,
  field,
  filter,
  onApply,
  testIdPrefix,
}: DateRangeFilterRowControlProps) {
  const { t } = useTranslation();
  // Tryb „pojedyncza data" trzymany lokalnie, żeby przetrwał moment gdy pole jest
  // puste (pusty filtr → `null` → `filter` undefined) i nie zgubić zaznaczenia.
  const [singleMode, setSingleMode] = useState<boolean>(filter?.singleDate ?? false);

  const fromValue = filter?.rangeMin ?? '';
  const toValue = filter?.rangeMax ?? '';
  const singleValue = filter?.rangeMin ?? '';

  const emitRange = (from: string, to: string) => {
    if (!from && !to) {
      onApply(null);
    } else {
      onApply({ field, mode: 'range', rangeMin: from || undefined, rangeMax: to || undefined });
    }
  };

  const emitSingle = (day: string) => {
    if (!day) {
      onApply(null);
    } else {
      onApply({ field, mode: 'range', rangeMin: day, rangeMax: day, singleDate: true });
    }
  };

  const handleToggleSingle = (checked: boolean) => {
    setSingleMode(checked);
    if (checked) {
      emitSingle(fromValue || toValue || '');
    } else {
      emitRange(singleValue, '');
    }
  };

  return (
    <div
      role="group"
      aria-label={`${t('ui.configurableTable.dateFilter.ariaLabel')}: ${columnLabel}`}
      className="flex items-center gap-2"
      data-testid={testIdPrefix}
    >
      {/* Checkbox INLINE obok pól daty (task #11) — cała kontrolka mieści się w JEDNEJ
          linii, więc komórka daty ma tę samą wysokość co reszta filtrów (Szukaj/Select)
          i wiersz filtrów jest wyrównany. */}
      <Checkbox
        checked={singleMode}
        onChange={(e) => handleToggleSingle(e.target.checked)}
        label={t('ui.configurableTable.dateFilter.singleDate')}
        className="mt-0 h-3.5 w-3.5"
        containerClassName="flex-shrink-0 [&_span]:whitespace-nowrap [&_span]:text-[11px] [&_span]:leading-none [&_span]:text-text-muted [&_label]:items-center"
        data-testid={testIdPrefix ? `${testIdPrefix}-single-toggle` : undefined}
      />
      {/* PRÓBKA #14: własny `DatePicker` (popover + stylowany kalendarz) zamiast
          natywnego `<input type=date>` (systemowy kalendarz OS). Wartość i emisja
          filtra bez zmian — nadal `YYYY-MM-DD`. */}
      {singleMode ? (
        <DatePicker
          value={singleValue}
          aria-label={`${columnLabel} — ${t('ui.configurableTable.dateFilter.date')}`}
          onChange={(val) => emitSingle(val)}
          className={`flex-1 ${FILTER_CONTROL_SIZE_CLASS}`}
          data-testid={testIdPrefix ? `${testIdPrefix}-single` : undefined}
        />
      ) : (
        <div className="flex flex-1 items-center gap-1">
          <DatePicker
            value={fromValue}
            aria-label={`${columnLabel} — ${t('ui.configurableTable.dateFilter.from')}`}
            onChange={(val) => emitRange(val, toValue)}
            className={`flex-1 ${FILTER_CONTROL_SIZE_CLASS}`}
            data-testid={testIdPrefix ? `${testIdPrefix}-from` : undefined}
          />
          <span className="text-[10px] text-text-muted">–</span>
          <DatePicker
            value={toValue}
            aria-label={`${columnLabel} — ${t('ui.configurableTable.dateFilter.to')}`}
            onChange={(val) => emitRange(fromValue, val)}
            className={`flex-1 ${FILTER_CONTROL_SIZE_CLASS}`}
            data-testid={testIdPrefix ? `${testIdPrefix}-to` : undefined}
          />
        </div>
      )}
    </div>
  );
}

// ============================================================
// SINGLE-CHOICE / MULTI-CHOICE — listy oparte na checkboxach
// ============================================================

interface ChoiceFilterRowControlProps {
  columnLabel: string;
  field: string;
  options: Array<{ value: string; label: string }>;
  filter: ColumnHeaderFilter | undefined;
  onApply: (next: ColumnHeaderFilter | null) => void;
  testIdPrefix?: string;
}

/** Aktualnie zaznaczone wartości filtra trybu `values` (jako Set stringów). */
function selectedValueSet(filter: ColumnHeaderFilter | undefined): Set<string> {
  if (filter?.mode !== 'values' || !filter.selectedValues) return new Set();
  return new Set(filter.selectedValues.filter((v): v is string => v !== null));
}

/**
 * Jednokrotny wybór (semantyka radio, renderowany checkboxami wg konwencji taska).
 * Zaznaczenie opcji zastępuje poprzednią; ponowny klik w zaznaczoną = wyczyść filtr.
 * Filtr = równość jednej wartości (`mode:'values'`, `selectedValues:[val]`), spójnie
 * z filtrem `select`. NIE domyślne dla żadnej kolumny — opt-in przez kategorię.
 */
export function SingleChoiceFilterRowControl({
  columnLabel,
  field,
  options,
  filter,
  onApply,
  testIdPrefix,
}: ChoiceFilterRowControlProps) {
  const selected = selectedValueSet(filter);
  const current = [...selected][0];
  return (
    <div
      role="group"
      aria-label={columnLabel}
      className="flex max-h-40 flex-col gap-0.5 overflow-y-auto"
      data-testid={testIdPrefix}
    >
      {options.map((opt) => {
        const isChecked = current === opt.value;
        return (
          <Checkbox
            key={opt.value}
            checked={isChecked}
            onChange={() =>
              onApply(isChecked ? null : { field, mode: 'values', selectedValues: [opt.value] })
            }
            label={opt.label}
            className="mt-0 h-3.5 w-3.5"
            containerClassName="[&_span]:text-xs [&_span]:leading-none [&_label]:items-center"
            data-testid={testIdPrefix ? `${testIdPrefix}-opt-${opt.value}` : undefined}
          />
        );
      })}
    </div>
  );
}

/**
 * Wielokrotny wybór — checkboxy, filtr = wartość ∈ zbiór zaznaczonych
 * (`mode:'values'`, `selectedValues:[...]`). Brak zaznaczeń = brak filtra.
 * NIE domyślne dla żadnej kolumny — opt-in przez kategorię.
 */
export function MultiChoiceFilterRowControl({
  columnLabel,
  field,
  options,
  filter,
  onApply,
  testIdPrefix,
}: ChoiceFilterRowControlProps) {
  const selected = selectedValueSet(filter);
  const emit = (nextSet: Set<string>) => {
    if (nextSet.size === 0) {
      onApply(null);
    } else {
      onApply({ field, mode: 'values', selectedValues: [...nextSet] });
    }
  };
  return (
    <div
      role="group"
      aria-label={columnLabel}
      className="flex max-h-40 flex-col gap-0.5 overflow-y-auto"
      data-testid={testIdPrefix}
    >
      {options.map((opt) => {
        const isChecked = selected.has(opt.value);
        return (
          <Checkbox
            key={opt.value}
            checked={isChecked}
            onChange={() => {
              const next = new Set(selected);
              if (next.has(opt.value)) next.delete(opt.value);
              else next.add(opt.value);
              emit(next);
            }}
            label={opt.label}
            className="mt-0 h-3.5 w-3.5"
            containerClassName="[&_span]:text-xs [&_span]:leading-none [&_label]:items-center"
            data-testid={testIdPrefix ? `${testIdPrefix}-opt-${opt.value}` : undefined}
          />
        );
      })}
    </div>
  );
}

// ============================================================
// DISPATCHER — jedna kontrolka filtra per kolumna
// ============================================================

interface ColumnFilterControlProps {
  columnLabel: string;
  /** Id kolumny (= pole `field` filtra). */
  columnId: string;
  /** Kategoria filtra z `column.headerFilter?.category` (może brakować). */
  category?: ColumnFilterCategory;
  /** Stała lista opcji dla kategorii `select`. */
  options?: Array<{ value: string; label: string }>;
  /** `column.filterable !== false`. */
  isFilterable: boolean;
  /** Aktualny header filter tej kolumny. */
  headerFilter: ColumnHeaderFilter | undefined;
  /** Zapis header filtra (już związany z `columnId` przez wołającego). */
  onApplyHeaderFilter: (next: ColumnHeaderFilter | null) => void;
  /** Wartość prostego filtra tekstowego (mapa `columnFilters`). */
  textValue: string;
  /** Zmiana prostego filtra tekstowego (już związana z `columnId`). */
  onTextChange: (value: string) => void;
  /** Prefiks `data-testid` (page-level). Konwencje sufiksów jak dotychczas. */
  testIdPrefix?: string;
}

/**
 * Dyspozytor: wybiera właściwą kontrolkę filtra po kategorii kolumny i utrzymuje
 * spójny wygląd (wszystkie oparte o `Input`/`Dropdown`). Zachowuje dotychczasowe
 * konwencje `data-testid` (`-datefilter-`/`-boolfilter-`/`-selectfilter-<id>`).
 */
export function ColumnFilterControl({
  columnLabel,
  columnId,
  category,
  options,
  isFilterable,
  headerFilter,
  onApplyHeaderFilter,
  textValue,
  onTextChange,
  testIdPrefix,
}: ColumnFilterControlProps) {
  const isDate = category === 'date';
  const isBoolean = category === 'boolean';
  const hasOptions = (options?.length ?? 0) > 0;
  const isSelect = category === 'select' && hasOptions;
  const isSingleChoice = category === 'single-choice' && hasOptions;
  const isMultiChoice = category === 'multi-choice' && hasOptions;

  if (isDate) {
    return (
      <DateRangeFilterRowControl
        columnLabel={columnLabel}
        field={columnId}
        filter={headerFilter}
        onApply={onApplyHeaderFilter}
        testIdPrefix={testIdPrefix ? `${testIdPrefix}-datefilter-${columnId}` : undefined}
      />
    );
  }

  if (isBoolean) {
    return (
      <BooleanFilterRowControl
        columnLabel={columnLabel}
        value={readBooleanFilterState(headerFilter)}
        onChange={(next) => {
          if (next === 'all') {
            onApplyHeaderFilter(null);
          } else {
            onApplyHeaderFilter({
              field: columnId,
              mode: 'condition',
              operator: 'eq',
              conditionValue: next,
            });
          }
        }}
        testIdPrefix={testIdPrefix ? `${testIdPrefix}-boolfilter-${columnId}` : undefined}
      />
    );
  }

  if (isSelect) {
    return (
      <SelectFilterRowControl
        columnLabel={columnLabel}
        field={columnId}
        options={options!}
        filter={headerFilter}
        onApply={onApplyHeaderFilter}
        testId={testIdPrefix ? `${testIdPrefix}-selectfilter-${columnId}` : undefined}
      />
    );
  }

  if (isSingleChoice) {
    return (
      <SingleChoiceFilterRowControl
        columnLabel={columnLabel}
        field={columnId}
        options={options!}
        filter={headerFilter}
        onApply={onApplyHeaderFilter}
        testIdPrefix={testIdPrefix ? `${testIdPrefix}-singlechoicefilter-${columnId}` : undefined}
      />
    );
  }

  if (isMultiChoice) {
    return (
      <MultiChoiceFilterRowControl
        columnLabel={columnLabel}
        field={columnId}
        options={options!}
        filter={headerFilter}
        onApply={onApplyHeaderFilter}
        testIdPrefix={testIdPrefix ? `${testIdPrefix}-multichoicefilter-${columnId}` : undefined}
      />
    );
  }

  if (isFilterable) {
    return (
      <SearchFilterRowControl columnLabel={columnLabel} value={textValue} onChange={onTextChange} />
    );
  }

  // Kolumna niefiltrowalna — spacer o wysokości kontrolki (spójny wiersz).
  return <div className="h-[38px]" />;
}
